import React, { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useConciergeContext } from '@brandclub/common-ui';

/**
 * Plan 7a / D-63 — back-compat for the retired `/concierge/voice/:conversationId`
 * route. Silently redirects to the unified `/concierge?conversationId=:id`
 * surface; the voice overlay can be re-opened from the input row's CallBenny
 * CTA on the same thread.
 *
 * Lives for at least one full release cycle so external deep links
 * (Slack notifications, history bookmarks, email follow-ups) keep
 * resolving. Removal is a separate cleanup ticket.
 *
 * IMPORTANT — why we set context state before navigating instead of using
 * a bare `<Navigate to=... />`:
 *
 * `ConciergeContextProvider` mounts ONCE at the top of `AppRoutes.tsx`
 * (above the `<Outlet />`). Its `useConversationState` initializes
 * `conversationId` from the URL via `useState(() => fromUrl())` — that
 * runs exactly once at mount, BEFORE this redirect's `<Navigate>` fires.
 * `useUrlSync` only writes state → URL, never URL → state. So a bare
 * `<Navigate to="/concierge?conversationId=:id" />` would change the URL
 * but leave `conversationId` state at `null`, and `useConversationLoader`
 * would render the welcome screen instead of the existing thread.
 *
 * Fix: set the id on context BEFORE the route change. React batches both
 * updates so the new `/concierge` route mounts with the correct
 * `conversationId` state and `useConversationLoader` immediately fetches.
 */
export const VoiceLegacyRedirect: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const conversationState = useConciergeContext();
  const navigate = useNavigate();
  // Guard against StrictMode double-mount re-running the side effect.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    if (conversationId) {
      // Clear any in-flight conversation state (mirrors
      // ChatHistory.handleConversationClick) so the loader's effect
      // runs cleanly against the redirected id.
      conversationState.setConversationHistory([]);
      conversationState.setFollowUpQuestions([]);
      conversationState.clearStreamingState();
      conversationState.setIsSharedView(false);
      conversationState.setSharedByUserName(undefined);
      conversationState.setSharedAtMessageIndex(undefined);
      conversationState.setSourceConversationId(undefined);
      conversationState.setSourceCheckpointIndex(undefined);
      conversationState.setSharedConversationId(null);
      conversationState.setConversationId(conversationId);
    }

    const target = conversationId
      ? `/concierge?conversationId=${encodeURIComponent(conversationId)}`
      : '/concierge';
    navigate(target, { replace: true });
  }, [conversationId, conversationState, navigate]);

  return null;
};

export default VoiceLegacyRedirect;
